import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import '../home.css';
import { useLang } from '../i18n';
import PublicNav from '../components/PublicNav';

// Tier discounts handled by /partner page and /pricing page

// Per-category info — icon + 2-3 line description
// Category names must match DB `category` column AFTER stripping "🇮🇹 Italy — " prefix
const CAT_INFO = {
    'Story Views': {
        icon: '👁️',
        desc: 'Real accounts watch your Stories — specific URL or all active Stories from your profile. Optional profile visit combo for maximum organic reach.',
        task_types: ['watch_story', 'watch_user_stories', 'watch_story_and_visit_profile'],
    },
    'Story Likes': {
        icon: '❤️',
        desc: 'Watch and like your Stories with verified engagement. Target a specific Story or have accounts browse all your active Stories and like randomly.',
        task_types: ['like_story', 'like_all_stories'],
    },
    'Story Shares': {
        icon: '🔄',
        desc: 'Real accounts share your Stories to their own Story — your content appears directly on their profile, reaching their entire follower base. The most powerful viral amplification signal.',
        task_types: ['share_specific_story', 'share_all_stories'],
    },
    'Story Reactions': {
        icon: '💬',
        desc: 'Emoji reactions or AI-generated text replies to your Stories — sent via DM or directly. The most visible and personal engagement signal, appearing right in your inbox.',
        task_types: ['react_story', 'react_specific_story', 'reply_story', 'reply_specific_story', 'react_story_dm', 'reply_story_dm'],
    },
    'Story Interactions': {
        icon: '🔗',
        desc: 'Real accounts interact with your Story stickers — click link, vote on polls, react to sliders, or reply to questions. AI vision automatically detects all sticker positions.',
        task_types: ['click_story_link', 'story_poll_vote', 'story_slider_vote', 'story_question_reply'],
    },
    'Reel Views': {
        icon: '🎬',
        desc: 'Genuine Reel views and engagement from real accounts with realistic watch time (60-90s). Watch time is the #1 signal for reaching the Explore page. Target a specific Reel or all recent Reels from your profile.',
        task_types: ['watch_specific_reel', 'watch_user_reels', 'like_reel', 'comment_reel'],
    },
    'Likes': {
        icon: '❤️',
        desc: 'Post and Reel likes from authentic accounts with real activity history. Target a specific post URL or your most recent posts by username. Gender targeting available.',
        task_types: ['like_post', 'like_reel', 'like_recent_post'],
    },
    'Followers': {
        icon: '👥',
        desc: 'Real followers — not empty shells. All accounts have posts, bio, and real activity history. Low natural drop rate. Gender targeting available for authentic audience demographics.',
        task_types: ['follow_user'],
    },
    'Profile Visits': {
        icon: '🔍',
        desc: 'Real accounts visit your profile and scroll through your grid for 30-60 seconds, simulating organic interest. Raises your discoverability score and pairs perfectly with story views.',
        task_types: ['visit_profile'],
    },
    'Save & Repost': {
        icon: '🔖',
        desc: 'Post saves and reposts — the strongest engagement signals in Instagram\'s algorithm. Saves indicate high-value content worth recommending. Reposts expose your content to new audiences organically.',
        task_types: ['save_post', 'repost_post', 'save_recent_post', 'repost_recent_post'],
    },
    'Share to Story': {
        icon: '📤',
        desc: 'Real accounts share your Posts or Reels to their own Story — your content appears directly on their followers\' feeds, driving organic traffic back to your profile.',
        task_types: ['share_post_to_story', 'share_reel_to_story'],
    },
    'Comments': {
        icon: '💬',
        desc: 'Unique AI-generated comments on posts and Reels, individually crafted using advanced language models. Each comment is contextually relevant and written naturally. The highest-quality engagement signal.',
        task_types: ['comment_post', 'comment_reel', 'comment_recent_post'],
    },
    'Custom Comments AI': {
        icon: '✍️',
        desc: 'You provide 1–20 example comments in your style — AI generates unique natural variations that preserve your exact tone, keywords, and brand voice. One AI call per order, zero per-comment cost. No clones, no repetition.',
        task_types: ['comment_post_enhanced', 'comment_reel_enhanced', 'comment_recent_post_enhanced'],
    },
    'Comment Likes': {
        icon: '👍',
        desc: 'Real accounts like comments on your posts — boosts comment visibility, signals authentic approval and rewards high-quality discussions under your content.',
        task_types: ['like_comment'],
    },
    'Growth Combos': {
        icon: '⚡',
        desc: 'Multiple high-value actions combined in a single session — likes, saves, comments, follows. Maximum engagement signal at a lower combined cost than buying separately.',
        task_types: ['watch_post_and_follow', 'watch_reel_and_follow', 'like_and_follow', 'like_save_and_follow', 'like_and_save', 'like_comment_and_follow', 'like_and_comment'],
    },
    'Direct Messages': {
        icon: '📨',
        desc: 'Send personalized AI-generated Direct Messages to any profile. Each DM is unique, friendly, and contextually natural — perfect for outreach, brand awareness, and building genuine connections.',
        task_types: ['send_dm', 'send_dm_enhanced'],
    },
};

// Display order for categories
const CAT_ORDER = [
    'Story Views',
    'Story Likes',
    'Story Shares',
    'Story Reactions',
    'Story Interactions',
    'Reel Views',
    'Likes',
    'Followers',
    'Profile Visits',
    'Save & Repost',
    'Share to Story',
    'Comments',
    'Custom Comments AI',
    'Comment Likes',
    'Growth Combos',
    'Direct Messages',
];

const LINK_LABEL   = { USERNAME: '@username', POST_URL: 'Post URL', STORY_URL: 'Story URL', REEL_URL: 'Reel URL' };

const SUB_PLANS = [
    { name: '1 Month',  serviceId: 2001, pricePerK: 650,  perAccountMonth: 0.65, hot: false, duration: '30 days' },
    { name: '3 Months', serviceId: 2002, pricePerK: 1700, perAccountMonth: 0.567, hot: true,  duration: '90 days', save: '13%' },
    { name: '6 Months', serviceId: 2003, pricePerK: 2900, perAccountMonth: 0.483, hot: false, duration: '180 days', save: '26%' },
];

const nav = {
    background: 'rgba(5,8,16,0.92)', backdropFilter: 'blur(16px)',
    borderBottom: '1px solid rgba(29,78,216,0.15)',
    position: 'sticky', top: 0, zIndex: 100,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 48px', gap: 16,
};
const navLinkStyle = { color: '#94a3b8', textDecoration: 'none', fontSize: 14, fontWeight: 500 };
const navLinkActive = { ...navLinkStyle, color: '#60a5fa', fontWeight: 600 };

export default function Services() {
    const navigate = useNavigate();
    const { fmtEur, t } = useLang();
    // reseller toggle removed — pricing tiers now on /partner page
    const [services, setServices]           = useState([]);
    const [deliveryStats, setDeliveryStats] = useState({});
    const [loading, setLoading]             = useState(true);

    const [serviceEta, setServiceEta]       = useState({});

    useEffect(() => {
        Promise.all([
            fetch('/public/services').then(r => r.json()).catch(() => []),
            fetch('/public/delivery-stats').then(r => r.json()).catch(() => ({ stats: {} })),
            fetch('/public/service-eta').then(r => r.json()).catch(() => ({ eta: {} })),
        ]).then(([svcs, ds, etaData]) => {
            setServices(Array.isArray(svcs) ? svcs : []);
            setDeliveryStats((ds && ds.stats) ? ds.stats : {});
            setServiceEta((etaData && etaData.eta) ? etaData.eta : {});
            setLoading(false);
        });
    }, []);

    // Group services by category (strip country prefix from DB categories)
    const grouped = {};
    for (const svc of services) {
        const cat = (svc.category || '').replace(/^.*?—\s*/, '');
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(svc);
    }
    const categories = CAT_ORDER.filter(c => grouped[c]);

    const fmtRate = (p1k) => fmtEur(p1k);
    const fmtPlan = (p)   => fmtEur(p);

    // Best delivery speed for a category (max of all its task_types)
    const getCatSpeed = (task_types) => {
        let best = 0;
        for (const tt of (task_types || [])) {
            const s = deliveryStats[tt];
            if (s && s.avg_display > best) best = s.avg_display;
        }
        return best || null;
    };

    const getSpeed = (task_type) => {
        const s = deliveryStats[task_type];
        return s ? { display: s.avg_display, isReal: s.is_real_data } : null;
    };

    const isLoggedIn = !!localStorage.getItem('token');
    const orderUrl   = isLoggedIn ? '/new-order' : '/register';

    const sv = t('services') || {};

    return (
        <div style={{ background: '#05080e', minHeight: '100vh', color: '#f1f5f9', fontFamily: '"Segoe UI", system-ui, sans-serif' }}>

            {/* ── NAV ──────────────────────────────────────────────────────── */}
            <PublicNav />

            {/* ── HERO ──────────────────────────────────────────────────────── */}
            <div style={{ padding: '72px 48px 40px', maxWidth: 1100, margin: '0 auto', textAlign: 'center' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(29,78,216,0.12)', border: '1px solid rgba(29,78,216,0.25)', borderRadius: 100, padding: '6px 18px', fontSize: 13, color: '#60a5fa', fontWeight: 600, marginBottom: 24 }}>
                    <span className="og-badge-live-dot" />
                    {sv.heroBadge || 'Instagram Engagement Services'}
                </div>
                <h1 style={{ fontSize: 52, fontWeight: 900, lineHeight: 1.1, letterSpacing: '-2px', marginBottom: 16 }}>
                    {sv.heroTitle1 || 'Real Engagement,'}<br />
                    <span className="og-shimmer-text">{sv.heroTitle2 || 'Authentic Growth'}</span>
                </h1>
                <p style={{ color: '#94a3b8', fontSize: 18, maxWidth: 580, margin: '0 auto 40px', lineHeight: 1.6 }}>
                    {sv.subtitle || '30+ services powered by real accounts. No bots, no empty profiles.'}
                </p>

                {/* Partner Program CTA */}
                <a href="/partner" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 24px', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: 100, color: '#a78bfa', fontSize: 14, fontWeight: 600, textDecoration: 'none', marginBottom: 12, transition: 'all 0.2s' }}>
                    🟣 Are you a reseller? Save up to 60% → View Partner Program
                </a>
            </div>

            {/* ── SERVICE CATEGORIES ────────────────────────────────────────── */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 80, color: '#475569', fontSize: 16 }}>{sv.loading || 'Loading services…'}</div>
            ) : (
                <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 48px 80px' }}>
                    {categories.map(cat => {
                        const info     = CAT_INFO[cat] || { icon: '📦', desc: '', task_types: [] };
                        const catSvcs  = grouped[cat] || [];
                        const catSpeed = getCatSpeed(info.task_types);
                        const catShort = cat.replace(/^.*?—\s*/, '');
                        const catDesc  = sv.cat_info?.[catShort] || info.desc;

                        return (
                            <div key={cat} style={{ marginBottom: 64 }}>
                                {/* Category header */}
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                    <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(29,78,216,0.12)', border: '1px solid rgba(29,78,216,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>
                                        {info.icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                                            <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.5px', margin: 0 }}>{catShort}</h2>
                                            {catSpeed && (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 20, padding: '4px 12px', fontSize: 12, color: '#86efac', fontWeight: 700 }}>
                                                    ⚡ ~{catSpeed.toLocaleString()}/h
                                                </span>
                                            )}
                                        </div>
                                        <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.65, margin: 0, maxWidth: 740 }}>{catDesc}</p>
                                    </div>
                                </div>

                                {/* Service variant cards */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 16 }}>
                                    {catSvcs.map(svc => {
                                        const spd     = getSpeed(svc.task_type);
                                        const svcName = svc.name.replace(/^IG /, '');
                                        const gender  = svc.gender || 'ALL';

                                        return (
                                            <div key={svc.service_id}
                                                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 20, transition: 'border-color 0.2s, box-shadow 0.2s', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 12 }}
                                                className="og-feature-card"
                                                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(29,78,216,0.4)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(29,78,216,0.1)'; }}
                                                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.boxShadow = 'none'; }}
                                            >
                                                {/* Name + gender badge */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', lineHeight: 1.35, flex: 1 }}>
                                                        {svcName}
                                                    </div>
                                                    <span style={{
                                                        fontSize: 11, borderRadius: 20, padding: '3px 9px', fontWeight: 600, flexShrink: 0,
                                                        background: gender === 'ALL' ? 'rgba(6,182,212,0.1)' : gender === 'FEMALE' ? 'rgba(236,72,153,0.1)' : 'rgba(59,130,246,0.1)',
                                                        border: `1px solid ${gender === 'ALL' ? 'rgba(6,182,212,0.3)' : gender === 'FEMALE' ? 'rgba(236,72,153,0.3)' : 'rgba(59,130,246,0.3)'}`,
                                                        color: gender === 'ALL' ? '#67e8f9' : gender === 'FEMALE' ? '#f9a8d4' : '#93c5fd',
                                                    }}>
                                                        {gender === 'ALL' ? (sv.genderAll || '♂♀ Mixed') : gender === 'FEMALE' ? (sv.genderFemale || '♀ Female') : (sv.genderMale || '♂ Male')}
                                                    </span>
                                                </div>

                                                {/* Meta badges */}
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                                    <span style={{ fontSize: 11, color: '#64748b', background: 'rgba(255,255,255,0.04)', borderRadius: 6, padding: '3px 8px' }}>
                                                        🔗 {LINK_LABEL[svc.link_type] || svc.link_type}
                                                    </span>
                                                    <span style={{ fontSize: 11, color: '#64748b', background: 'rgba(255,255,255,0.04)', borderRadius: 6, padding: '3px 8px' }}>
                                                        📦 {svc.min_qty}–{svc.max_qty}
                                                    </span>
                                                    <span style={{ fontSize: 11, color: '#f87171', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, padding: '3px 8px' }}>
                                                        🚫 {sv.noRefill || 'No Refill'}
                                                    </span>
                                                    {spd && (() => {
                                                        const eta = spd.display > 0 ? Math.ceil(1000 / spd.display) : null;
                                                        const etaLabel = eta ? (eta < 24 ? `~${eta}h` : `~${Math.round(eta / 24)}d`) : '';
                                                        return (
                                                            <span style={{ fontSize: 11, borderRadius: 6, padding: '3px 8px', border: '1px solid', color: spd.isReal ? '#86efac' : '#94a3b8', background: spd.isReal ? 'rgba(34,197,94,0.07)' : 'rgba(255,255,255,0.04)', borderColor: spd.isReal ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.07)' }}>
                                                                ⚡ ~{spd.display}/h{etaLabel ? ` · ${etaLabel}/1K` : ''} {spd.isReal ? `(${sv.speedLive || 'live'})` : `(${sv.speedEst || 'est.'})`}
                                                            </span>
                                                        );
                                                    })()}
                                                    {(() => {
                                                        const etaInfo = serviceEta[svc.task_type] || { label: 'Starting now', start_min: 0 };
                                                        const isNow = etaInfo.label === 'Starting now';
                                                        return (
                                                            <span style={{ fontSize: 11, borderRadius: 6, padding: '3px 8px', border: '1px solid', color: isNow ? '#86efac' : '#fbbf24', background: isNow ? 'rgba(34,197,94,0.07)' : 'rgba(251,191,36,0.07)', borderColor: isNow ? 'rgba(34,197,94,0.2)' : 'rgba(251,191,36,0.2)' }}>
                                                                🕐 {etaInfo.label}
                                                            </span>
                                                        );
                                                    })()}
                                                </div>

                                                {/* Price + CTA */}
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 'auto' }}>
                                                    <div>
                                                        <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.5px', color: '#f1f5f9' }}>
                                                            {fmtRate(parseFloat(svc.price_per_1k))}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: '#475569' }}>{sv.perThousand || 'per 1,000'}</div>
                                                    </div>
                                                    <button onClick={() => navigate(orderUrl)}
                                                        style={{ padding: '9px 18px', background: 'linear-gradient(135deg,#1d4ed8,#06b6d4)', border: 'none', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                        {sv.orderNow || 'Order →'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── SUBSCRIPTION PLANS ────────────────────────────────────────── */}
            <div style={{ background: 'rgba(29,78,216,0.04)', borderTop: '1px solid rgba(29,78,216,0.1)', borderBottom: '1px solid rgba(29,78,216,0.1)', padding: '80px 48px' }}>
                <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: 48 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#1d4ed8', marginBottom: 12 }}>
                            {sv.plans || 'Subscription Plans'}
                        </div>
                        <h2 style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-1px', marginBottom: 12 }}>{sv.subTitle || 'Automatic Daily Growth'}</h2>
                        <p style={{ color: '#94a3b8', fontSize: 16, maxWidth: 640, margin: '0 auto' }}>
                            {sv.subDesc || 'Choose how many Instagram accounts will follow and interact with your profile daily. Each account performs likes, follows, story views, and profile visits — organically, every day.'}
                        </p>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24, overflow: 'visible', paddingTop: 16 }} className="og-plan-grid">
                        {SUB_PLANS.map(p => (
                            <div key={p.name}
                                style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${p.hot ? 'rgba(29,78,216,0.5)' : 'rgba(255,255,255,0.08)'}`, borderRadius: 16, padding: 28, paddingTop: p.hot ? 36 : 28, position: 'relative', transform: p.hot ? 'translateY(-8px)' : 'none', transition: 'transform 0.25s' }}
                                className={`og-plan-card${p.hot ? ' og-plan-hot' : ''}`}
                            >
                                {p.hot && (
                                    <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg,#1d4ed8,#06b6d4)', borderRadius: 20, padding: '5px 18px', fontSize: 11, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', zIndex: 1 }}>
                                        {sv.bestValue || 'Best Value'}
                                    </div>
                                )}
                                {p.save && (
                                    <div style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 8, padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#86efac' }}>
                                        {sv.savePct || 'Save'} {p.save}
                                    </div>
                                )}
                                <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>{p.name === '1 Month' ? (sv.plan1m || p.name) : p.name === '3 Months' ? (sv.plan3m || p.name) : (sv.plan6m || p.name)}</div>
                                <div style={{ marginBottom: 8 }}>
                                    <span style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-1px' }}>{fmtEur(p.pricePerK)}</span>
                                    <span style={{ fontSize: 14, color: '#64748b' }}>{sv.perKAccounts || '/1,000 accounts'}</span>
                                </div>
                                <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 20 }}>
                                    {fmtEur(p.perAccountMonth)}{sv.perAccountMonth || '/account/month'}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
                                    {[
                                        sv.subF1 || 'Daily likes on your posts',
                                        sv.subF2 || 'Follow your profile',
                                        sv.subF3 || 'Daily story views',
                                        sv.subF4 || 'Profile visits & reel views',
                                        sv.subF5 || 'Real verified accounts',
                                        `${sv.duration || 'Duration'}: ${p.duration}`,
                                    ].map(f => (
                                        <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#94a3b8' }}>
                                            <span style={{ color: '#1d4ed8', fontWeight: 700 }}>✓</span>{f}
                                        </div>
                                    ))}
                                </div>
                                <button onClick={() => navigate(isLoggedIn ? `/new-order?service=${p.serviceId}` : '/register')}
                                    style={{ width: '100%', padding: '12px', background: p.hot ? 'linear-gradient(135deg,#1d4ed8,#06b6d4)' : 'rgba(29,78,216,0.15)', border: `1px solid ${p.hot ? 'transparent' : 'rgba(29,78,216,0.3)'}`, borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                                    {p.hot ? (sv.startGrowing || 'Start Growing →') : (sv.getStarted || 'Get Started →')}
                                </button>
                            </div>
                        ))}
                    </div>
                    <div style={{ textAlign: 'center', marginTop: 24, fontSize: 13, color: '#475569' }}>
                        {sv.subNote || 'Price shown per 1,000 accounts — choose your desired quantity at checkout (min 10). Stackable.'}
                    </div>
                </div>
            </div>

            {/* ── CTA ───────────────────────────────────────────────────────── */}
            <div style={{ padding: '80px 48px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
                <div className="og-cta-glow" />
                <h2 style={{ fontSize: 40, fontWeight: 900, letterSpacing: '-1.5px', marginBottom: 16 }}>
                    {sv.ctaTitle || 'Ready to orbit your growth?'}
                </h2>
                <p style={{ color: '#94a3b8', fontSize: 16, maxWidth: 480, margin: '0 auto 32px' }}>
                    {sv.ctaSub || 'Join thousands of creators and agencies growing with OrbitGrowth.'}
                </p>
                <button onClick={() => navigate('/register')} className="og-btn-primary" style={{ fontSize: 17, padding: '16px 48px' }}>
                    {sv.ctaBtn || 'Create Free Account →'}
                </button>
                <div style={{ marginTop: 16, fontSize: 12, color: '#334155' }}>
                    {sv.ctaNote || 'No subscription required. Pay only for what you order. Cancel anytime.'}
                </div>
                {t('home')?.ctaTrial && (
                    <div style={{ marginTop: 14, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', background: 'rgba(6,182,212,0.1)', border: '1px solid rgba(6,182,212,0.25)', borderRadius: 100, fontSize: 13, color: '#67e8f9', fontWeight: 600 }}>
                        {t('home').ctaTrial}
                    </div>
                )}
            </div>

            {/* ── FOOTER ────────────────────────────────────────────────────── */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '32px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                <div style={{ color: '#334155', fontSize: 13 }}>© 2026 OrbitGrowth. {sv.footerRights || 'All rights reserved.'}</div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                    {[['Home', '/'], ['Pricing', '/pricing'], ['Services', '/services'], ['API Docs', '/api-docs'], ['Terms', '/terms'], ['Login', '/login']].map(([label, path]) => (
                        <Link key={path} to={path} style={{ color: '#475569', fontSize: 12, textDecoration: 'none' }}>{label}</Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
